import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Edit, Trash2, Eye, EyeOff, Loader2 } from 'lucide-react';
import adminApi from '@/lib/adminApi';

export default function AdminBlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await adminApi.get('/api/admin/blog');
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch blog posts:', error);
      /* list stays empty when the fetch fails */
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    setDeleting(postId);
    
    try {
      await adminApi.delete(`/api/admin/blog/${postId}`);
      setPosts(posts.filter(p => p.id !== postId));
    } catch (error) {
      alert('Failed to delete post');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="admin-blog-list">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">Blog Posts</h1>
        <Link to="/admin/blog/new" className="btn-primary">
          + New Post
        </Link>
      </div>

      {posts.length === 0 ? (
        <div className="text-center py-12 border border-[#27272A] bg-[#0C0C0C]">
          <p className="text-[#A1A1AA] mb-4">No blog posts yet</p>
          <Link to="/admin/blog/new" className="text-[#007AFF] hover:underline">
            Create your first post →
          </Link>
        </div>
      ) : (
        <div className="border border-[#27272A] bg-[#0C0C0C] overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#27272A]">
                <th className="text-left p-4 text-[#A1A1AA] text-sm font-medium">Title</th>
                <th className="text-left p-4 text-[#A1A1AA] text-sm font-medium hidden md:table-cell">Status</th>
                <th className="text-left p-4 text-[#A1A1AA] text-sm font-medium hidden lg:table-cell">Date</th>
                <th className="text-right p-4 text-[#A1A1AA] text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((post) => (
                <tr key={post.id} className="border-b border-[#27272A] last:border-0 hover:bg-[#121212]">
                  <td className="p-4">
                    <div>
                      <p className="font-medium">{post.title}</p>
                      <p className="text-[#52525B] text-sm truncate max-w-xs">{post.excerpt}</p>
                    </div>
                  </td>
                  <td className="p-4 hidden md:table-cell">
                    {post.published ? (
                      <span className="inline-flex items-center gap-1 text-green-400 text-sm">
                        <Eye size={14} />
                        Published
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[#52525B] text-sm">
                        <EyeOff size={14} />
                        Draft
                      </span>
                    )}
                  </td>
                  <td className="p-4 text-[#A1A1AA] text-sm hidden lg:table-cell">
                    {new Date(post.created_at).toLocaleDateString('en-AU')}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/admin/blog/edit/${post.id}`}
                        className="p-2 text-[#A1A1AA] hover:text-[#007AFF] transition-colors"
                        title="Edit"
                        data-testid={`edit-post-${post.id}`}
                      >
                        <Edit size={18} />
                      </Link>
                      <button
                        onClick={() => handleDelete(post.id)}
                        disabled={deleting === post.id}
                        className="p-2 text-[#A1A1AA] hover:text-red-400 transition-colors disabled:opacity-50"
                        title="Delete"
                        data-testid={`delete-post-${post.id}`}
                      >
                        {deleting === post.id ? (
                          <Loader2 size={18} className="animate-spin" />
                        ) : (
                          <Trash2 size={18} />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
